import { v4 as uuidV4 } from "uuid";

import { CRSResource } from "src/api/connectApi/types";
import {
  defaultResourceConfig,
  getDefaultOutputMapping,
  getNamedOutputMapping,
} from "src/integrationNode/integrationResources/DefaultResourceConfig";
import {
  CommonOutputMappingKey,
  getCommonOutputMappings,
} from "src/integrationNode/integrationResources/common";
import {
  InputMappingGroup,
  InputMappingList,
  InputMapping,
  IntegrationResource,
} from "src/integrationNode/types";

const getDefaultCRSExperianCreditReportAddress = (): {
  [key: string]: InputMapping;
} => ({
  street: {
    id: uuidV4(),
    type: "text",
    displayName: "Street",
    assignedTo: "",
    rules: {
      required: true,
    },
    hint: `1-48 characters, only a-z, A-Z, 0-9, and -⎵./#`,
  },
  affix: {
    id: uuidV4(),
    type: "text",
    displayName: "Affix",
    assignedTo: "",
    hint: `The house number affix (e.g., "APT 6", "# 5"). 0-60 characters, only a-z, A-Z, 0-9, and -⎵./#`,
  },
  postal_code: {
    id: uuidV4(),
    type: "text",
    displayName: "Postal code",
    assignedTo: "",
    rules: {
      required: true,
    },
    hint: `5-10 character postal or zip code`,
  },
  city: {
    id: uuidV4(),
    type: "text",
    displayName: "City",
    assignedTo: "",
    rules: {
      required: true,
    },
    hint: `1-28 characters, only a-z, A-Z and spaces allowed`,
  },
  state: {
    id: uuidV4(),
    type: "text",
    displayName: "State",
    assignedTo: "",
    rules: {
      required: true,
    },
    hint: `A two-letter code for the U.S. state or territory of the address. Note that some U.S. states require more data for identifying a consumer than others. For example, Rhode Island prohibits using only the SSN to identify a person.`,
  },
});

const getDefaultCRSExperianBusinessSearchAddress = (): {
  [key: string]: InputMapping;
} => {
  const { street, postal_code, city, state } =
    getDefaultCRSExperianCreditReportAddress();

  street.rules = {
    ...street.rules,
    required: false,
  };
  postal_code.rules = {
    ...postal_code.rules,
    required: false,
  };

  city.hint = undefined;
  street.hint = undefined;

  return { street, postal_code, city, state };
};

const getDefaultCRSPhoneInput = (): InputMappingGroup => ({
  getDefaultElements: () => ({
    number: {
      id: uuidV4(),
      type: "text",
      displayName: "Number",
      assignedTo: "",
      rules: {
        required: true,
      },
      hint: `10-digit phone number (e.g., "1112223334")`,
    },
  }),
});

const getDefaultCRSAddressList = (type: CRSResource): InputMappingList => {
  const addressFunc =
    type === "experian_business_search"
      ? getDefaultCRSExperianBusinessSearchAddress
      : getDefaultCRSExperianCreditReportAddress;

  return {
    displayName: "address",
    rules: {
      required: true,
    },
    getDefaultElement: addressFunc,
    elements: [addressFunc()],
  };
};

export const getDefaultCRSExperianCreditReport = (): IntegrationResource => ({
  providerResource: {
    provider: "crs",
    resource: "experian_credit_report",
  },
  connectionId: "",
  resourceConfigId: "",
  input: {
    ungrouped: {
      first_name: {
        id: uuidV4(),
        type: "text",
        displayName: "First name",
        assignedTo: "",
        rules: {
          required: true,
        },
        hint: `1-20 characters`,
      },
      middle_name: {
        id: uuidV4(),
        type: "text",
        displayName: "Middle name",
        assignedTo: "",
        hint: `1-20 characters, only one word allowed.`,
      },
      last_name: {
        id: uuidV4(),
        type: "text",
        displayName: "Last name",
        assignedTo: "",
        rules: {
          required: true,
        },
        hint: `1-32 characters`,
      },
      name_suffix: {
        id: uuidV4(),
        type: "text",
        displayName: "Name suffix",
        assignedTo: "",
        hint: `One of: Jr, Sr, III, IV, V, VI, VII, VIII, IX, X`,
      },
      date_of_birth: {
        id: uuidV4(),
        type: "text",
        displayName: "Date of birth",
        assignedTo: "",
        hint: `Must adhere to the ISO format "YYYY-MM-DD", e.g., "1960-12-20". Omitting this field may lead to a "weak match": Parts of the summaries section of the expected report response may be missing.`,
      },
      social_security_number: {
        id: uuidV4(),
        type: "text",
        displayName: "Social security number",
        assignedTo: "",
        hint: `The individual's 9-digit social security number (SSN), formatted as a simple string of integers (e.g., "123456789").`,
      },
    },
    grouped: {
      phone: getDefaultCRSPhoneInput(),
    },
    lists: {
      addresses: getDefaultCRSAddressList("experian_credit_report"),
    },
  },
  output: {
    default: getDefaultOutputMapping(),
    insights: {
      ...getCommonOutputMappings(
        CommonOutputMappingKey.PROVIDER_REQUEST_ID,
        CommonOutputMappingKey.RETRIEVAL_DATETIME,
      ),
      fico_8_score: {
        ...getNamedOutputMapping("FICO 8 Score"),
        hint: `The FICO Score is a credit score that is widely used by lenders to assess a person's creditworthiness. The score ranges from 300 to 850 and is based on higher scores indicating better credit health.
      The main difference between FICO 8 and FICO 9 is how they treat medical debt collections and rent payments. In FICO 9, medical collections have less impact, and rent payments are taken into account, helping with thin-file credit assessment.
      Note that it depends on your agreement with CRS which of the scores will be part of the response.`,
      },
      fico_9_score: {
        ...getNamedOutputMapping("FICO 9 Score"),
        hint: `The FICO Score is a credit score that is widely used by lenders to assess a person's creditworthiness. The score ranges from 300 to 850 and is based on higher scores indicating better credit health.
        The main difference between FICO 8 and FICO 9 is how they treat medical debt collections and rent payments. In FICO 9, medical collections have less impact, and rent payments are taken into account, helping with thin-file credit assessment.
        Note that it depends on your agreement with CRS which of the scores will be part of the response.`,
      },
      vantage_4_score: {
        ...getNamedOutputMapping("Vantage 4 Score"),
        hint: `VantageScore is a credit score that ranges from 300 to 850 and is used to assess a person's creditworthiness. A higher VantageScore indicates a better credit history, with a score above 700 generally considered good, and scores over 800 excellent.`,
      },
    },
  },
  config: defaultResourceConfig,
});

const getDefaultCRSEquifaxAddress = (): InputMappingGroup => ({
  getDefaultElements: () => ({
    house_number: {
      id: uuidV4(),
      type: "text",
      displayName: "House number",
      assignedTo: "",
      rules: {
        required: false,
        maxLength: 10,
      },
    },
    quadrant: {
      id: uuidV4(),
      type: "text",
      displayName: "Quadrant",
      assignedTo: "",
      hint: "e.g. NW, NE, etc.",
      rules: {
        required: false,
        maxLength: 2,
      },
    },
    street: {
      id: uuidV4(),
      type: "text",
      displayName: "Street",
      assignedTo: "",
      rules: {
        required: false,
        maxLength: 26,
      },
    },
    street_type: {
      id: uuidV4(),
      type: "text",
      displayName: "Street type",
      assignedTo: "",
      rules: {
        required: false,
      },
    },
    apartment_number: {
      id: uuidV4(),
      type: "text",
      displayName: "Apartment number",
      assignedTo: "",
      hint: "A string value for the apartment/condo/unit number",
      rules: {
        required: false,
        maxLength: 5,
      },
    },
    city: {
      id: uuidV4(),
      type: "text",
      displayName: "City",
      assignedTo: "",
      rules: {
        required: true,
      },
    },
    state: {
      id: uuidV4(),
      type: "text",
      displayName: "State",
      assignedTo: "",
      hint: "The two-letter code of the U.S. state or territory of the address",
      rules: {
        required: true,
      },
    },
    postal_code: {
      id: uuidV4(),
      type: "text",
      displayName: "Postal code",
      assignedTo: "",
      hint: "A string value for the numeric postal code (e.g., 12345)",
      rules: {
        required: false,
        maxLength: 9,
      },
    },
  }),
});

export const getDefaultCRSEquifaxCreditReport = (): IntegrationResource => ({
  providerResource: {
    provider: "crs",
    resource: "crs_equifax_credit_report",
  },
  connectionId: "",
  resourceConfigId: "",
  input: {
    ungrouped: {
      first_name: {
        id: uuidV4(),
        type: "text",
        displayName: "First name",
        assignedTo: "",
        rules: {
          required: true,
          maxLength: 15,
        },
      },
      middle_name: {
        id: uuidV4(),
        type: "text",
        displayName: "Middle name",
        assignedTo: "",
        hint: `Only one word allowed`,
        rules: {
          required: false,
          maxLength: 15,
        },
      },
      last_name: {
        id: uuidV4(),
        type: "text",
        displayName: "Last name",
        assignedTo: "",
        rules: {
          required: true,
          maxLength: 25,
        },
      },
      suffix: {
        id: uuidV4(),
        type: "text",
        displayName: "Name suffix",
        assignedTo: "",
        hint: "e.g. Jr, Sr, etc.",
        rules: {
          required: false,
          maxLength: 2,
        },
      },
      age: {
        id: uuidV4(),
        type: "text",
        displayName: "Age",
        assignedTo: "",
        rules: {
          required: false,
          maxLength: 2,
        },
      },
      date_of_birth: {
        id: uuidV4(),
        type: "text",
        displayName: "Date of birth",
        assignedTo: "",
        hint: "Format: YYYY-MM-DD",
        rules: {
          required: false,
        },
      },
      social_security_number: {
        id: uuidV4(),
        type: "text",
        displayName: "Social security number",
        hint: `The individual's 9-digit social security number (SSN), formatted as a simple string of integers (e.g., "123456789").`,
        assignedTo: "",
        rules: {
          required: false,
          maxLength: 9,
        },
      },
      phone_number: {
        id: uuidV4(),
        type: "text",
        displayName: "Phone number",
        assignedTo: "",
        hint: "10 digit number (e.g., 0000000000)",
        rules: {
          required: false,
          maxLength: 10,
        },
      },
      occupation: {
        id: uuidV4(),
        type: "text",
        displayName: "Occupation",
        assignedTo: "",
        hint: "Professional role or job title (e.g., Software Engineer)",
        rules: {
          required: false,
          maxLength: 35,
        },
      },
      employer_name: {
        id: uuidV4(),
        type: "text",
        displayName: "Employer name",
        assignedTo: "",
        rules: {
          required: false,
          maxLength: 35,
        },
      },
    },
    grouped: {
      address: getDefaultCRSEquifaxAddress(),
    },
    lists: {},
  },
  output: {
    default: getDefaultOutputMapping(),
    insights: {
      ...getCommonOutputMappings(
        CommonOutputMappingKey.PROVIDER_REQUEST_ID,
        CommonOutputMappingKey.RETRIEVAL_DATETIME,
      ),
      fico_9_score: {
        ...getNamedOutputMapping("FICO 9 Score"),
        hint: `The FICO 9 score ranges from 300 to 850 and is based on higher scores indicating better credit health.
        The main difference between FICO 8 and FICO 9 is how they treat medical debt collections and rent payments. In FICO 9, medical collections have less impact, and rent payments are taken into account, helping with thin-file credit assessment.
        Note that the FICO 9 score is only available if you include 'efx-prequal-fico9' as the configuration key during resource configuration.`,
      },
      vantage_4_score: {
        ...getNamedOutputMapping("Vantage 4 Score"),
        hint: `VantageScore is a credit score that ranges from 300 to 850 and is used to assess a person's creditworthiness. A higher VantageScore indicates a better credit history, with a score above 700 generally considered good, and scores over 800 excellent. Note that the Vantage 4 score is only available if you include 'efx-prequal-vantage4' as the configuration key during resource configuration.`,
      },
      hit_code: {
        ...getNamedOutputMapping("Hit code"),
        hint: `'1' for a regular hit, '2' for no hit, 'I' for a fraudulent or misused report, 'A' for a frozen report. All possible hit codes can be found in CRS Equifax documentation.`,
      },
    },
  },
  config: defaultResourceConfig,
});

export const getDefaultCRSExperianBusinessSearch = (): IntegrationResource => ({
  providerResource: {
    provider: "crs",
    resource: "experian_business_search",
  },
  connectionId: "",
  resourceConfigId: "",
  input: {
    ungrouped: {
      company_name: {
        id: uuidV4(),
        type: "text",
        displayName: "Company name",
        assignedTo: "",
        rules: {
          required: true,
        },
      },
      tax_id: {
        id: uuidV4(),
        type: "text",
        displayName: "Tax ID",
        assignedTo: "",
        hint: `A federal tax identification number, also known as an Employer Identification Number (EIN), is utilized to identify a business entity, as well as estates and trusts that are required to report their income on Form 1041, U.S. Income Tax Return for Estates and Trusts.`,
      },
      minimum_reliability_code: {
        id: uuidV4(),
        type: "text",
        displayName: "Minimum reliability code",
        assignedTo: "",
        hint: `Entering a value here will filter out any result that has a reliability code below this value. The code ranges from 0 to 100.4, with a low-confidence score being below 80, a medium-confidence being 80-90, and high being 90-100.4. Adding more details of the company in your search will improve results.`,
      },
    },
    grouped: {
      phone: getDefaultCRSPhoneInput(),
    },
    lists: {
      addresses: getDefaultCRSAddressList("experian_business_search"),
    },
  },
  output: {
    default: getDefaultOutputMapping(),
    insights: {
      ...getCommonOutputMappings(
        CommonOutputMappingKey.PROVIDER_REQUEST_ID,
        CommonOutputMappingKey.RETRIEVAL_DATETIME,
      ),
      most_confident_bin: {
        ...getNamedOutputMapping("Most confident BIN"),
        hint: `The Business Identity Number or BIN is a unique identifier used to identify a business. This report returns all businesses and BINs that match the search parameters provided. This insight provides the BIN that the search identifies as the most likely match.`,
      },
      highest_reliability_code: {
        ...getNamedOutputMapping("Highest reliability code"),
        hint: `This code signifies the confidence with which Experian believes that the top result is in fact the company being searched for. The code ranges from 0 to 100.4, with a low-confidence score being below 80, a medium-confidence being 80-90, and high being 90-100.4. Adding more details of the company in your search will improve results.`,
      },
    },
  },
  config: defaultResourceConfig,
});

const getBinExperianInput = (): InputMapping => ({
  id: uuidV4(),
  type: "text",
  displayName: "BIN",
  assignedTo: "",
  rules: {
    required: true,
  },
  hint: `A unique 9-digit business identification number assigned by Experian. This is not the same as a Tax ID.`,
});

export const getDefaultCRSBusinessProfileReport = (): IntegrationResource => ({
  providerResource: {
    provider: "crs",
    resource: "experian_business_profile_report",
  },
  connectionId: "",
  resourceConfigId: "",
  input: {
    ungrouped: {
      bin: getBinExperianInput(),
    },
    grouped: {},
    lists: {},
  },
  output: {
    default: getDefaultOutputMapping(),
    insights: {
      ...getCommonOutputMappings(
        CommonOutputMappingKey.PROVIDER_REQUEST_ID,
        CommonOutputMappingKey.RETRIEVAL_DATETIME,
      ),
    },
  },
  config: defaultResourceConfig,
});

export const getDefaultCRSIntellisenseBusinessReport =
  (): IntegrationResource => ({
    providerResource: {
      provider: "crs",
      resource: "experian_intelliscore_business_report",
    },
    connectionId: "",
    resourceConfigId: "",
    input: {
      ungrouped: {
        bin: getBinExperianInput(),
      },
      grouped: {},
      lists: {},
    },
    output: {
      default: getDefaultOutputMapping(),
      insights: {
        ...getCommonOutputMappings(
          CommonOutputMappingKey.PROVIDER_REQUEST_ID,
          CommonOutputMappingKey.RETRIEVAL_DATETIME,
        ),
        intelliscore: {
          ...getNamedOutputMapping("Intelliscore"),
          hint: `Predicts the likelihood of seriously derogatory payment within the next 12 months. Values range from 1 to 100, 1 indicating a high risk and 100 a low risk. There are also two exclusion scores: a 998 score is returned when there is bankruptcy within the last two years, a 999 score is returned when there is not enough information to score a business.`,
        },
      },
    },
    config: defaultResourceConfig,
  });

const getDefaultCrsTransunionAddress = (): Record<string, InputMapping> => ({
  street: {
    id: uuidV4(),
    type: "text",
    displayName: "Street",
    assignedTo: "",
    rules: {
      required: true,
    },
  },
  house_number: {
    id: uuidV4(),
    type: "text",
    displayName: "House number",
    assignedTo: "",
  },
  city: {
    id: uuidV4(),
    type: "text",
    displayName: "City",
    assignedTo: "",
    hint: "The city is only required if the address cannot be identified via other given information (e.g., the postal code)",
  },
  postal_code: {
    id: uuidV4(),
    type: "text",
    displayName: "Postal code",
    assignedTo: "",
    hint: `5-10 characters`,
  },
  state: {
    id: uuidV4(),
    type: "text",
    displayName: "State",
    assignedTo: "",
    hint: `A two-letter code for the US state or territory of the address.`,
  },
});

export const getDefaultCrsTransunionCreditReport = (): IntegrationResource => ({
  providerResource: { provider: "crs", resource: "transunion_credit_report" },
  connectionId: "",
  resourceConfigId: "",
  input: {
    ungrouped: {
      first_name: {
        id: uuidV4(),
        type: "text",
        displayName: "First name",
        assignedTo: "",
        rules: {
          required: true,
        },
      },
      middle_name: {
        id: uuidV4(),
        type: "text",
        displayName: "Middle name",
        assignedTo: "",
        hint: `Only one word allowed`,
      },
      last_name: {
        id: uuidV4(),
        type: "text",
        displayName: "Last name",
        assignedTo: "",
        rules: {
          required: true,
        },
      },
      social_security_number: {
        id: uuidV4(),
        type: "text",
        displayName: "Social security number",
        assignedTo: "",
        hint: `The individual's 9-digit social security number (SSN), formatted as a simple string of integers (e.g., "123456789").`,
      },
    },
    grouped: {},
    lists: {
      addresses: {
        displayName: "address",
        getDefaultElement: getDefaultCrsTransunionAddress,
        elements: [getDefaultCrsTransunionAddress()],
      },
    },
  },
  output: {
    default: getDefaultOutputMapping(),
    insights: {
      ...getCommonOutputMappings(
        CommonOutputMappingKey.PROVIDER_REQUEST_ID,
        CommonOutputMappingKey.RETRIEVAL_DATETIME,
      ),
      vantage_score: {
        ...getNamedOutputMapping("VantageScore"),
        hint: `VantageScore is a credit score that ranges from 300 to 850 and is used to assess a person's creditworthiness. A higher VantageScore indicates a better credit history, with a score above 700 generally considered good, and scores over 800 excellent.`,
      },
      number_of_inquiries: {
        ...getNamedOutputMapping("Number of inquiries"),
        hint: `The number of times a person's credit report has been accessed by potential lenders or creditors within a certain period.`,
      },
      number_of_trades: {
        ...getNamedOutputMapping("Number of trades"),
        hint: `Number of trades in credit reports refers to the total number of credit accounts or loans that a person has on their credit report.`,
      },
      months_of_oldest_tradeline: {
        ...getNamedOutputMapping("Months of oldest tradeline"),
        hint: `Trade lines are records of credit accounts that appear on a person's credit report. They show the credit history of the account, including payment history, credit limits, and balances.`,
      },
      utilization_ratio: {
        ...getNamedOutputMapping("Utilization ratio"),
        hint: `The credit utilization ratio is a significant factor in credit reporting that measures the amount of credit a person is using relative to their credit limit. It's calculated by dividing a person's credit card balances by their total credit limit, expressed as a percentage.`,
      },
      months_since_bankruptcy: getNamedOutputMapping("Months since bankruptcy"),
      months_since_charge_off: {
        ...getNamedOutputMapping("Months since charge-off"),
        hint: `Charge-offs are a negative item that can appear on a person's credit report when a lender or creditor writes off a debt as uncollectible. This occurs when a borrower fails to make payments for an extended period, typically 180 days or more.`,
      },
      derogatory_alert: {
        ...getNamedOutputMapping("Derogatory alert"),
        hint: `A derogatory alert is a negative item that appears on a person's credit report when there is a potential issue with their credit history. It can include information such as late payments, collections, charge-offs, bankruptcies, or other negative events.`,
      },
      max_number_months_delinquent: getNamedOutputMapping(
        "Max number months delinquent",
      ),
      credit_counselling_usage_count: {
        ...getNamedOutputMapping("Credit counselling usage count"),
        hint: `Credit counselling is a non-profit service that helps individuals manage their debt and improve their credit by negotiating with creditors to establish a debt management plan.`,
      },
      mortgage_delinquencies: getNamedOutputMapping("Mortgage delinquencies"),
    },
  },
  config: defaultResourceConfig,
});

const getCrsTransunionIMVBasicAddress = (): {
  [key: string]: InputMapping;
} => ({
  street: {
    id: uuidV4(),
    type: "text",
    displayName: "Street",
    assignedTo: "",
    rules: {
      required: true,
    },
    hint: `1-48 characters. The house number is also expected here (e.g., "123 Main St")`,
  },
  postal_code: {
    id: uuidV4(),
    type: "text",
    displayName: "Postal code",
    assignedTo: "",
    rules: {
      required: true,
    },
    hint: `5-10 characters.`,
  },
  city: {
    id: uuidV4(),
    type: "text",
    displayName: "City",
    assignedTo: "",
    rules: {
      required: true,
    },
    hint: `1-28 characters.`,
  },
  state: {
    id: uuidV4(),
    type: "text",
    displayName: "State",
    assignedTo: "",
    rules: {
      required: true,
    },
    hint: `A two-letter code for the U.S. state or territory of the address. Note that some U.S. states require more data for identifying a consumer than others. For example, Rhode Island prohibits using only the SSN to identify a person.`,
  },
});

const getDefaultCrsTransunionIMVBasicAddress = (): InputMappingGroup => ({
  getDefaultElements: () => getCrsTransunionIMVBasicAddress(),
  elements: getCrsTransunionIMVBasicAddress(),
  rules: {
    required: true,
  },
});

export const getDefaultCrsTransunionIMVBasic = (): IntegrationResource => ({
  providerResource: { provider: "crs", resource: "transunion_imv_basic" },
  connectionId: "",
  resourceConfigId: "",
  input: {
    ungrouped: {
      first_name: {
        id: uuidV4(),
        type: "text",
        displayName: "First name",
        assignedTo: "",
        rules: {
          required: true,
        },
        hint: `1-20 characters, only a-z, A-Z, 0-9, and -⎵./#`,
      },
      middle_name: {
        id: uuidV4(),
        type: "text",
        displayName: "Middle name",
        assignedTo: "",
        hint: `1-20 characters, only a-z, A-Z, 0-9, and -⎵./#`,
      },
      last_name: {
        id: uuidV4(),
        type: "text",
        displayName: "Last name",
        assignedTo: "",
        rules: {
          required: true,
        },
        hint: `1-32 characters, only a-z, A-Z, 0-9, and -⎵./#`,
      },
      name_suffix: {
        id: uuidV4(),
        type: "text",
        displayName: "Name suffix",
        assignedTo: "",
        hint: `One of: Jr, Sr, III, IV, V, VI, VII, VIII, IX, X`,
      },
      date_of_birth: {
        id: uuidV4(),
        type: "text",
        displayName: "Date of birth",
        assignedTo: "",
        hint: `Must adhere to the ISO format "YYYY-MM-DD", e.g., "1960-12-20".`,
      },
      social_security_number: {
        id: uuidV4(),
        type: "text",
        displayName: "Social security number",
        assignedTo: "",
        hint: `The individual's social security number (SSN), formatted as a simple string of integers (e.g., "123456789").`,
      },
      phone: {
        id: uuidV4(),
        type: "text",
        displayName: "Phone number",
        assignedTo: "",
        rules: {
          required: true,
        },
        hint: `10-digit phone number (e.g., "1112223334")`,
      },
    },
    grouped: {
      address: getDefaultCrsTransunionIMVBasicAddress(),
    },
    lists: {},
  },
  output: {
    default: getDefaultOutputMapping(),
    insights: {
      ...getCommonOutputMappings(),
    },
  },
  config: defaultResourceConfig,
});

const getDefaultCRSLexisNexisBridgerSearchAddress = (): InputMappingGroup => ({
  getDefaultElements: () => ({
    street: {
      id: uuidV4(),
      type: "text",
      displayName: "Street",
      assignedTo: "",
    },
    postal_code: {
      id: uuidV4(),
      type: "text",
      displayName: "Postal code",
      assignedTo: "",
    },
    city: {
      id: uuidV4(),
      type: "text",
      displayName: "City",
      assignedTo: "",
    },
    country: {
      id: uuidV4(),
      type: "text",
      displayName: "Country",
      assignedTo: "",
      hint: `Two-letter country code according to ISO 3166-1 alpha-2 (e.g, "DE" for Germany)`,
    },
  }),
});

export const getDefaultCRSLexisNexisBridgerSearch =
  (): IntegrationResource => ({
    providerResource: {
      provider: "crs",
      resource: "lexisnexis_bridger_search",
    },
    connectionId: "",
    resourceConfigId: "",
    input: {
      ungrouped: {
        first_name: {
          id: uuidV4(),
          type: "text",
          displayName: "First name",
          assignedTo: "",
          rules: {
            required: true,
          },
        },
        last_name: {
          id: uuidV4(),
          type: "text",
          displayName: "Last name",
          assignedTo: "",
          rules: {
            required: true,
          },
        },
        search_type: {
          id: uuidV4(),
          type: "text",
          displayName: "Search type",
          assignedTo: "",
          rules: {
            required: true,
          },
          hint: `One of: "NMS_PEP_OFAC_SANC", "List Screening"`,
        },
        date_of_birth: {
          id: uuidV4(),
          type: "text",
          displayName: "Date of birth",
          assignedTo: "",
          hint: `Must adhere to the ISO format "YYYY-MM-DD", e.g., "1960-12-20".`,
        },
        social_security_number: {
          id: uuidV4(),
          type: "text",
          displayName: "Social security number",
          assignedTo: "",
        },
        phone: {
          id: uuidV4(),
          type: "text",
          displayName: "Phone number",
          assignedTo: "",
        },
        glb_purpose: {
          id: uuidV4(),
          type: "text",
          displayName: "GLB purpose",
          assignedTo: "",
          rules: {
            required: true,
          },
          hint: `The purpose for which the information is being obtained accordingly to the Gramm-Leach-Bliley Act. Refer to our docs for further details. One of: "0", "1", "3", "5", "6", "7", "12"`,
        },
        dl_purpose: {
          id: uuidV4(),
          type: "text",
          displayName: "DL purpose",
          assignedTo: "",
          rules: {
            required: true,
          },
          hint: `The purpose for which the Driver's license data is being obtained. Refer to our docs for further details. One of: "0", "1", "2", "3", "4", "6"`,
        },
        minimum_entity_score: {
          id: uuidV4(),
          type: "text",
          displayName: "Minimum entity score",
          assignedTo: "",
          hint: `Indicates the minimum entity score required for watchlists in order to be included in the respective insights. Ranges from 0 - 100.`,
        },
      },
      grouped: {
        address: getDefaultCRSLexisNexisBridgerSearchAddress(),
      },
      lists: {},
    },
    output: {
      default: getDefaultOutputMapping(),
      insights: {
        ...getCommonOutputMappings(),
        reasons_listed: getNamedOutputMapping(
          "Reasons listed",
          `Provides a list of all the reasons the person was part of a watchlist item. If "minimum entity score" is passed, only the items over the given score are taken into consideration. Some possible values are: "Adverse Media:Murder", "PEP:Associate", "Sanction List".`,
        ),
      },
    },
    config: defaultResourceConfig,
  });
